import React, { useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Terminal-window code surface matching the API guide on the deployment
// detail page (same palette constants). Theme-aware: GitHub-dark canvas
// in dark mode, GitHub-light canvas in light mode.
const CODE_PALETTES = {
  dark: {
    bg: '#0d1117',
    headerBg: '#161b22',
    border: '#30363d',
    text: '#d7dce3',
    muted: '#8b949e',
    copyHover: '#fff',
  },
  light: {
    bg: '#f6f8fa',
    headerBg: '#eaeef2',
    border: '#d0d7de',
    text: '#1f2328',
    muted: '#57606a',
    copyHover: '#1f2328',
  },
};
const CODE_DOTS = ['#ff5f56', '#ffbd2e', '#27c93f'];

const CopyChip = ({ text, title = 'Copy', onCopied, palette }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    try {
      navigator.clipboard.writeText(text);
      if (onCopied) onCopied();
    } catch (_e) { /* clipboard blocked */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Tooltip title={copied ? 'Copied!' : title} arrow>
      <IconButton
        size="small"
        onClick={onCopy}
        sx={{ color: copied ? '#27c93f' : palette.muted, '&:hover': { color: palette.copyHover } }}
      >
        {copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

const CodeSurface = ({ label, code, wrap = false, onCopied, sx }) => {
  const theme = useTheme();
  const palette = CODE_PALETTES[theme.palette.mode === 'dark' ? 'dark' : 'light'];
  return (
    <Box sx={{ borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${palette.border}`, ...sx }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pl: 1.5, pr: 0.5, py: 0.5, bgcolor: palette.headerBg, borderBottom: `1px solid ${palette.border}` }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Stack direction="row" spacing={0.5}>
            {CODE_DOTS.map((c) => (
              <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c, opacity: 0.9 }} />
            ))}
          </Stack>
          <Typography variant="caption" sx={{ color: palette.muted, fontWeight: 600, letterSpacing: 0.4 }}>
            {label}
          </Typography>
        </Stack>
        <CopyChip text={code} onCopied={onCopied} palette={palette} />
      </Stack>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          overflowX: 'auto',
          bgcolor: palette.bg,
          color: palette.text,
          fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
          fontSize: 12.5,
          lineHeight: 1.7,
          whiteSpace: wrap ? 'pre-wrap' : 'pre',
          wordBreak: wrap ? 'break-all' : 'normal',
          userSelect: 'all',
        }}
      >
        {code}
      </Box>
    </Box>
  );
};

export default CodeSurface;
